import { contextUsagePercent } from "@/lib/context-usage";
import type { DurableConversation } from "@/api/conversations";
import {
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { AgentCursorArrow } from "@/components/cursors/agent-cursor-arrow";
import { ContextRing } from "@/components/ui/context-ring";
import { conversationDisplayName } from "@/lib/conversation-display";
import { formatModelLabel } from "@/lib/model-registry";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useCompactAgentDisplayStore } from "@/stores/compact-agent-display-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import { useT, formatRelativeTime } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

interface CompactRecentChatsCardProps {
  chats: DurableConversation[];
  onKeepOpen: () => void;
  onRequestClose: (event: PointerEvent<HTMLDivElement>) => void;
  onSelectChat: (chat: DurableConversation) => void;
  onViewAllChats?: () => void;
  onShowShortcuts?: () => void;
}

type ChatControlTooltipKind = "search" | "shortcuts" | "dock" | "speech";

interface ChatControlTooltipState {
  kind: ChatControlTooltipKind;
  left: number;
  top: number;
}

const CHAT_CONTROL_TOOLTIP_ID = "desktop-compact-chat-control-tooltip";

function chatModelLabel(chat: DurableConversation): string | null {
  const model = chat.frame_runtime_segment?.model;
  if (!model) return null;
  return formatModelLabel(undefined, model);
}

export function CompactRecentChatsCard({
  chats,
  onKeepOpen,
  onRequestClose,
  onSelectChat,
  onViewAllChats,
  onShowShortcuts,
}: CompactRecentChatsCardProps) {
  const t = useT(compactMessages);
  return (
    <div
      className="scrollbar-none min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto p-2"
      data-testid="desktop-compact-chat-card"
      role="menu"
      onPointerEnter={onKeepOpen}
      onPointerLeave={(event) => {
        event.stopPropagation();
        onRequestClose(event);
      }}
    >
      {chats.length === 0 ? (
        <p
          className="px-3 py-4 text-center text-canvas-sm text-muted"
          data-testid="desktop-compact-chat-empty"
        >
          {t("noRecentChats")}
        </p>
      ) : null}
      {chats.map((chat) => (
        <CompactChatRow key={chat.id} chat={chat} onSelectChat={onSelectChat} />
      ))}
      <CompactChatFooterControls
        onViewAllChats={onViewAllChats}
        onShowShortcuts={onShowShortcuts}
      />
    </div>
  );
}

function CompactChatFooterControls({
  onViewAllChats,
  onShowShortcuts,
}: {
  onViewAllChats?: () => void;
  onShowShortcuts?: () => void;
}) {
  const dockEnabled = useCompactAgentDisplayStore((state) => state.dockEnabled);
  const toggleDockEnabled = useCompactAgentDisplayStore(
    (state) => state.toggleDockEnabled,
  );
  const speakReplies = useTtsSettingsStore((state) => state.enabled);
  const toggleSpeakReplies = useTtsSettingsStore(
    (state) => state.toggleEnabled,
  );
  const t = useT(compactMessages);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<ChatControlTooltipState | null>(null);
  const tooltipLabel = tooltip
    ? tooltip.kind === "search"
      ? t("searchAllChats")
      : tooltip.kind === "shortcuts"
        ? t("keyboardShortcuts")
        : tooltip.kind === "dock"
          ? dockEnabled
            ? t("undockCursor")
            : t("dockCursor")
          : speakReplies
            ? t("muteReplies")
            : t("speakReplies")
    : null;

  useLayoutEffect(() => {
    const element = tooltipRef.current;
    if (!element || !tooltip) return;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0) return;
    const edgePadding = 8;
    const correction =
      rect.left < edgePadding
        ? edgePadding - rect.left
        : rect.right > window.innerWidth - edgePadding
          ? window.innerWidth - edgePadding - rect.right
          : 0;
    if (correction === 0) return;
    setTooltip((current) =>
      current ? { ...current, left: current.left + correction } : current,
    );
  }, [tooltip]);

  const showTooltip = (
    kind: ChatControlTooltipKind,
    target: HTMLButtonElement,
  ) => {
    const rect = target.getBoundingClientRect();
    const viewportPadding = 64;
    setTooltip({
      kind,
      left: Math.min(
        Math.max(rect.left + rect.width / 2, viewportPadding),
        window.innerWidth - viewportPadding,
      ),
      top: rect.top - 8,
    });
  };

  const tooltipHandlers = (kind: ChatControlTooltipKind) => ({
    "aria-describedby": CHAT_CONTROL_TOOLTIP_ID,
    onBlur: () => setTooltip(null),
    onFocus: (event: FocusEvent<HTMLButtonElement>) =>
      showTooltip(kind, event.currentTarget),
    onPointerEnter: (event: PointerEvent<HTMLButtonElement>) =>
      showTooltip(kind, event.currentTarget),
    onPointerLeave: () => setTooltip(null),
    onContextMenu: () => setTooltip(null),
  });

  return (
    <div
      className="flex items-center justify-between pt-1"
      data-testid="desktop-compact-chat-controls"
    >
      <div className="flex items-center gap-0.5">
        {onViewAllChats ? (
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className="text-muted hover:bg-white/10 hover:text-primary focus-visible:bg-white/10 focus-visible:text-primary"
            aria-label={t("searchAllChats")}
            data-testid="desktop-compact-view-all-chats"
            role="menuitem"
            {...tooltipHandlers("search")}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onViewAllChats();
            }}
          >
            <Icons.Search className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        ) : null}
        {onShowShortcuts ? (
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className="text-muted hover:bg-white/10 hover:text-primary focus-visible:bg-white/10 focus-visible:text-primary"
            aria-label={t("keyboardShortcuts")}
            data-testid="desktop-compact-shortcuts"
            role="menuitem"
            {...tooltipHandlers("shortcuts")}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onShowShortcuts();
            }}
          >
            <Icons.Info className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className={cn(
            "text-muted hover:bg-white/10 hover:text-primary focus-visible:bg-white/10 focus-visible:text-primary",
            dockEnabled && "text-accent",
          )}
          aria-label={dockEnabled ? t("undockCursor") : t("dockCursor")}
          aria-pressed={dockEnabled}
          data-state={dockEnabled ? "docked" : "undocked"}
          data-testid="desktop-compact-chat-toggle-dock"
          role="menuitem"
          {...tooltipHandlers("dock")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleDockEnabled();
          }}
        >
          <span
            className="grid h-4 w-4 place-items-center [--agent-cursor-scale:1]"
            data-testid="desktop-compact-chat-dock-cursor-icon"
          >
            <AgentCursorArrow
              color="currentColor"
              width={10}
              height={13}
              mode="flat"
              shape="soft"
              position="relative"
              tipTestId="desktop-compact-chat-dock-cursor-shape"
            />
          </span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className="text-muted hover:bg-white/10 hover:text-primary focus-visible:bg-white/10 focus-visible:text-primary"
          aria-label={speakReplies ? t("muteReplies") : t("speakReplies")}
          aria-pressed={speakReplies}
          data-testid="desktop-compact-chat-toggle-speech"
          role="menuitem"
          {...tooltipHandlers("speech")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleSpeakReplies();
          }}
        >
          {speakReplies ? (
            <Icons.Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Icons.VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </Button>
      </div>
      {tooltip &&
        tooltipLabel &&
        createPortal(
          <div
            ref={tooltipRef}
            id={CHAT_CONTROL_TOOLTIP_ID}
            role="tooltip"
            className="denker-glass-tooltip pointer-events-none fixed z-[100] max-w-[calc(100vw-16px)] -translate-x-1/2 -translate-y-full break-words rounded-control border px-2.5 py-1 text-center text-canvas-md font-medium text-primary"
            data-testid={CHAT_CONTROL_TOOLTIP_ID}
            style={{ left: tooltip.left, top: tooltip.top }}
          >
            {tooltipLabel}
          </div>,
          document.body,
        )}
    </div>
  );
}

function CompactChatRow({
  chat,
  onSelectChat,
}: {
  chat: DurableConversation;
  onSelectChat: (chat: DurableConversation) => void;
}) {
  const modelLabel = chatModelLabel(chat);
  const tokenKey = chat.agent_name
    ? `${chat.agent_name.toLowerCase()}:${chat.project_id}`
    : null;
  const sessionTokens = useCompanionStore((state) =>
    tokenKey ? state.sessionTokens[tokenKey] : undefined,
  );
  const segment = chat.frame_runtime_segment;
  const liveMatches = Boolean(
    sessionTokens &&
    (!sessionTokens.runtimeSessionId ||
      !segment?.runtime_session_id ||
      sessionTokens.runtimeSessionId === segment.runtime_session_id) &&
    (!sessionTokens.conversationId ||
      sessionTokens.conversationId === chat.id) &&
    ((segment?.runtime_session_id &&
      sessionTokens.runtimeSessionId === segment.runtime_session_id) ||
      sessionTokens.conversationId === chat.id),
  );
  const live = liveMatches ? sessionTokens : undefined;
  const contextPercent = contextUsagePercent({
    used: live ? live.contextUsed : segment?.context_used_tokens,
    input: live ? live.lastInput : segment?.input_tokens,
    output: live ? live.lastOutput : segment?.output_tokens,
    max: live ? live.max : segment?.context_window_max,
    fallbackPercent: live ? undefined : segment?.context_percent,
  });
  return (
    <button
      type="button"
      className="flex h-12 min-w-0 w-full flex-col justify-center gap-0.5 rounded-[16px] px-3 py-1.5 text-left transition-colors hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none"
      data-testid={`desktop-compact-chat-${chat.id}`}
      role="menuitem"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSelectChat(chat);
      }}
    >
      <span className="block min-w-0 truncate text-canvas-md font-medium leading-tight text-primary">
        {conversationDisplayName(chat)}
      </span>
      <span className="flex min-w-0 items-center gap-1.5 text-canvas-sm leading-tight text-muted">
        {modelLabel ? (
          <span className="min-w-0 flex-1 truncate">{modelLabel}</span>
        ) : null}
        {contextPercent !== null ? (
          <ContextRing
            percent={contextPercent}
            size={13}
            className="shrink-0"
            data-testid={`desktop-compact-chat-${chat.id}-context`}
          />
        ) : null}
        <span className="shrink-0">
          {formatRelativeTime(chat.activity_at, { style: "narrow" })}
        </span>
      </span>
    </button>
  );
}
